import type { Badge } from '@src/typings/badge'
import { getEarnedRibbonDisplayCount } from '@src/utils/gamification.util'

/** Source-agnostic badge presentation for CDP progress UI. */
export interface ProgressBadgeDisplay {
  imageUrl?: string | null
  name?: string | null
  /** Already-resolved ribbon count; null hides the ribbon. */
  earnedCount?: number | null
}

export const getBadgeEntityImageUrl = (badge: CocoApi.BadgeEntity): string | null => {
  if (badge.url) return badge.url

  const status = badge.userData?.status ?? badge.status
  if (!status) return null

  const statusArtwork = badge.content?.[status]?.imageBadge
  if (!statusArtwork) return null

  return statusArtwork
}

// Preference order: the path only checks off required items, and a day can hold
// more than one workout.
const PROGRESS_COUNT_PAIRS = [
  ['totalRequiredWorkouts', 'totalRequiredCompletedWorkouts'],
  ['totalRequiredDays', 'totalRequiredCompletedDays'],
  ['totalWorkouts', 'completedWorkouts'],
  ['totalDays', 'completedDays'],
] as const

export type BadgeProgress = { completed: number; total: number }

export const getBadgeEntityProgress = (badge: CocoApi.BadgeEntity): BadgeProgress => {
  const progress = badge.userData?.progress

  for (const [totalKey, completedKey] of PROGRESS_COUNT_PAIRS) {
    const total = progress?.[totalKey]
    if (typeof total === 'number' && total > 0) {
      return { completed: progress?.[completedKey] ?? 0, total }
    }
  }

  return { completed: 0, total: 0 }
}

const downgradeActive = (
  status: CocoApi.BadgeEntityStatus | undefined
): CocoApi.BadgeEntityStatus | undefined => (status === 'active' ? 'available' : status)

// Downgrading active hides the reset action immediately. earnedCount and
// completedDate stay: resetting progress does not un-earn a badge.
export const resetBadgeEntityProgress = (badge: CocoApi.BadgeEntity): CocoApi.BadgeEntity => ({
  ...badge,
  status: downgradeActive(badge.status),
  userData: {
    ...badge.userData,
    status: downgradeActive(badge.userData?.status),
    completedDayIds: [],
    nextItem: null,
    progress: badge.userData?.progress
      ? {
          ...badge.userData.progress,
          totalRequiredCompletedWorkouts: 0,
          totalRequiredCompletedDays: 0,
          completedWorkouts: 0,
          completedDays: 0,
        }
      : badge.userData?.progress,
  },
})

export const resolveTrackProgressCounts = (
  badgeProgress: BadgeProgress | null,
  gamificationBadge: Badge | null,
  scheduleProgress: BadgeProgress
): BadgeProgress => {
  if (badgeProgress?.total) return badgeProgress

  if (!gamificationBadge) return scheduleProgress

  const total =
    [gamificationBadge.count, gamificationBadge.badgeItems?.length].find((value) => !!value) ?? 0

  if (total) {
    const completed =
      gamificationBadge.currentCount ?? total - (gamificationBadge.remainingItems?.length ?? 0)

    return { completed, total }
  }
  return scheduleProgress
}

const resolveNextItemEntityId = (
  nextItem: NonNullable<CocoApi.BadgeEntityUserData['nextItem']>,
  entities: CocoApi.Entities | undefined
): string | null => {
  if (!entities) return nextItem._id ?? null
  if (nextItem._id && entities[nextItem._id]) return nextItem._id

  if (nextItem.slug) {
    const match = Object.values(entities).find(
      (entity) => (entity as { slug?: string })?.slug === nextItem.slug
    ) as { _id?: string } | undefined
    if (match?._id) return match._id
  }

  return nextItem._id ?? null
}

// Requires a dayId: a workout can be scheduled more than once.
export const getBadgeEntityNextItem = (
  badge: CocoApi.BadgeEntity | null | undefined,
  entities?: CocoApi.Entities
): { entityId: string; dayId: string } | null => {
  const nextItem = badge?.userData?.nextItem
  if (!nextItem?.dayId) return null

  const entityId = resolveNextItemEntityId(nextItem, entities)
  if (!entityId) return null

  return { entityId, dayId: nextItem.dayId }
}

export const badgeEntityToProgressDisplay = (
  badge: CocoApi.BadgeEntity | null | undefined
): ProgressBadgeDisplay | null => {
  if (!badge) return null

  const status = badge.userData?.status ?? badge.status

  return {
    imageUrl: getBadgeEntityImageUrl(badge),
    name: badge.title ?? badge.slug ?? '',
    earnedCount: status
      ? getEarnedRibbonDisplayCount({ earnedCount: badge.userData?.earnedCount, status })
      : null,
  }
}

export const gamificationBadgeToProgressDisplay = (
  badge: Badge | null | undefined
): ProgressBadgeDisplay | null => {
  if (!badge) return null

  return {
    imageUrl: badge.url || null,
    name: badge.name ?? badge.slug ?? '',
    earnedCount: getEarnedRibbonDisplayCount(badge),
  }
}
